import {
  formatMonitoringPercent,
  PERCENT_DECIMALS,
} from 'containers/user/critical-elements-monitoring/helpers/format-monitoring-value';
import type {
  CompareTo,
  ExtendWithOption,
  FunnelStageKey,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export const MONITORING_TAB_QUERY_KEY = 'tab';
export const MONITORING_GOALS_TAB_VALUE = 'goals';
export const MONITORING_GOALS_TAB_PATH = `/monitoring?${MONITORING_TAB_QUERY_KEY}=${MONITORING_GOALS_TAB_VALUE}`;

export type StageDisplayMode = 'volume' | 'rate' | 'proportion';

export const STAGE_LABELS: Record<FunnelStageKey, string> = {
  inquiry: 'Inquiry Volume',
  application: 'Application Volume',
  admit: 'Admit Volume',
  netDeposit: 'Net Deposit Volume',
};

export const FUNNEL_STAGE_COUNT = Object.keys(STAGE_LABELS).length;

export type ComparatorKey = 'client' | 'cohort' | 'vsCohort';

export const COMPARATOR_UNIT: Record<ComparatorKey, string> = {
  client: '%',
  cohort: '%',
  vsCohort: ' pp',
};

export const COMPARATOR_ORDER: readonly ComparatorKey[] = [
  'client',
  'cohort',
  'vsCohort',
];

const COMPARATOR_PP_FIELD: Record<
  ComparatorKey,
  'clientPp' | 'cohortPp' | 'vsCohortPp'
> = {
  client: 'clientPp',
  cohort: 'cohortPp',
  vsCohort: 'vsCohortPp',
};

const COMPARATOR_PROPORTION_FIELD: Record<
  ComparatorKey,
  'clientProportion' | 'cohortProportion' | 'vsCohortProportion'
> = {
  client: 'clientProportion',
  cohort: 'cohortProportion',
  vsCohort: 'vsCohortProportion',
};

export const getComparatorFieldForMode = (
  key: ComparatorKey,
  displayMode: StageDisplayMode
):
  | ComparatorKey
  | 'clientPp'
  | 'cohortPp'
  | 'vsCohortPp'
  | 'clientProportion'
  | 'cohortProportion'
  | 'vsCohortProportion' => {
  if (displayMode === 'rate') return COMPARATOR_PP_FIELD[key];
  if (displayMode === 'proportion') return COMPARATOR_PROPORTION_FIELD[key];

  return key;
};

export const getComparatorUnit = (
  key: ComparatorKey,
  displayMode: StageDisplayMode
): string =>
  displayMode === 'rate' || displayMode === 'proportion'
    ? ' pp'
    : COMPARATOR_UNIT[key];

export const NO_VALUE_GLYPH = '—';

export const ZERO_BASE_COMPARATOR_TEXT = 'N/A';

export const NO_MONITORING_DATA_MESSAGE =
  'There is no monitoring data available for this Critical Element yet.';

const PROGRESS_TO_FINAL_SUFFIX = ' (% to final)';

const PROGRESS_TO_FINAL_BASE = 100;

export const getComparatorLabel = (
  key: ComparatorKey,
  cohortLabel: string,
  isProgressToFinal = false
): string => {
  if (key === 'vsCohort') return 'My School vs Cohort';

  if (isProgressToFinal) {
    const base = key === 'client' ? 'My School' : cohortLabel;

    return `${base}${PROGRESS_TO_FINAL_SUFFIX}`;
  }

  if (key === 'client') return 'My School (YoY)';

  return cohortLabel;
};

export const formatProgressToFinal = (delta: number): string => {
  const roundedDelta = Number(delta.toFixed(PERCENT_DECIMALS));
  const sign = roundedDelta > 0 ? '+' : '';

  return `${sign}${formatMonitoringPercent(
    PROGRESS_TO_FINAL_BASE + roundedDelta
  )}`;
};

export const BAR_PAIR_LABELS: Record<
  CompareTo,
  { prior: string; current: string }
> = {
  'point-in-time': {
    prior: 'Same Point Last Year',
    current: 'Same Point This Year',
  },
  'prior-year': {
    prior: 'Prior Year Final',
    current: 'Current Year Final',
  },
  'last-7-days': {
    prior: '7 days ago last year',
    current: '7 days ago this year',
  },
  'last-30-days': {
    prior: '30 days ago last year',
    current: '30 days ago this year',
  },
};

const STAGE_RATE_LABELS: Record<FunnelStageKey, string> = {
  inquiry: 'Inquiry Rate',
  application: 'Application Rate',
  admit: 'Admit Rate',
  netDeposit: 'Yield Rate',
};

const STAGE_PROPORTION_LABELS: Record<FunnelStageKey, string> = {
  inquiry: 'Inquiry Proportion',
  application: 'Application Proportion',
  admit: 'Admit Proportion',
  netDeposit: 'Net Deposit Proportion',
};

export const getStageDisplayLabel = (
  stageKey: FunnelStageKey,
  displayMode: StageDisplayMode
): string => {
  if (displayMode === 'rate') return STAGE_RATE_LABELS[stageKey];
  if (displayMode === 'proportion') return STAGE_PROPORTION_LABELS[stageKey];

  return STAGE_LABELS[stageKey];
};

export const ALL_EXTEND_WITH_OPTIONS: readonly ExtendWithOption[] = [
  'prospect',
  'financial-aid',
  'retention',
];

export const EXTEND_WITH_PRODUCT_LABEL: Record<ExtendWithOption, string> = {
  prospect: 'Prospect View',
  'financial-aid': 'Financial Aid View',
  retention: 'Retention View',
};

export const EXTEND_WITH_UPSELL_DESCRIPTION: Record<ExtendWithOption, string> =
  {
    prospect:
      'See performance breakdowns by prospect source to evaluate the efficiency of your pipeline. Contact Client Success for more information.',
    'financial-aid':
      'See comparative FAFSA filing data and discounting trends to strengthen your pricing strategy. Contact Client Success for more information.',
    retention:
      'See individual student-level risk scores to identify attrition risk before it happens. Contact Client Success for more information.',
  };

export const STAGE_BASE_LABEL: Record<FunnelStageKey, string> = {
  inquiry: 'Inquiry',
  application: 'Application',
  admit: 'Admit',
  netDeposit: 'Net Deposit',
};

export const EXTEND_WITH_BASE_LABEL: Record<ExtendWithOption, string> = {
  prospect: 'Prospect',
  'financial-aid': 'Filer',
  retention: 'Retention',
};

const MODE_SUFFIX: Record<StageDisplayMode, string> = {
  volume: 'Volume',
  rate: 'Rate',
  proportion: 'Proportion',
};

export const FUNNEL_PANEL_CLASSES =
  'rounded-[10px] border border-navy-100 bg-white p-[13px] shadow-sm';

const EXTENSION_RATE_LABEL_OVERRIDE: Partial<Record<ExtendWithOption, string>> =
  {
    prospect: 'Prospect to App Rate',
  };

export const getExtensionDisplayLabel = (
  type: ExtendWithOption,
  displayMode: StageDisplayMode
): string => {
  const rateLabelOverride = EXTENSION_RATE_LABEL_OVERRIDE[type];

  if (displayMode === 'rate' && rateLabelOverride) return rateLabelOverride;

  return `${EXTEND_WITH_BASE_LABEL[type]} ${MODE_SUFFIX[displayMode]}`;
};

export const EXTENSION_COHORT_METRICS_UNAVAILABLE: Record<
  ExtendWithOption,
  boolean
> = {
  prospect: true,
  'financial-aid': true,
  retention: true,
};

export const STAGE_COHORT_METRICS_UNAVAILABLE: Record<FunnelStageKey, boolean> =
  {
    inquiry: true,
    application: false,
    admit: true,
    netDeposit: true,
  };

const isExtensionKey = (
  key: FunnelStageKey | ExtendWithOption
): key is ExtendWithOption => key in EXTENSION_COHORT_METRICS_UNAVAILABLE;

export const isFunnelRowCohortUnavailable = (
  key: FunnelStageKey | ExtendWithOption,
  displayMode: StageDisplayMode
): boolean => {
  if (isExtensionKey(key)) return EXTENSION_COHORT_METRICS_UNAVAILABLE[key];

  return key === 'inquiry' && displayMode !== 'rate'
    ? false
    : STAGE_COHORT_METRICS_UNAVAILABLE[key];
};
